import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Crown, Sparkles, Loader2, Check, Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { usePro } from "@/context/ProContext";

const FEATURES = [
  "Monte Carlo simulator with AI narratives",
  "Full Parlay Builder w/ same-game correlation",
  "Daily Picks filtered by EV + credibility",
  "Closing Line Value (CLV) dashboard",
  "14-factor self-learning engine",
];

// Cache the Stripe.js promise so we don't re-init on every render.
let _stripePromise = null;
const getStripePromise = async () => {
  if (_stripePromise) return _stripePromise;
  const { data } = await api.get("/payments/config");
  const pk = data?.publishable_key;
  if (!pk) throw new Error("Stripe publishable key missing");
  _stripePromise = loadStripe(pk);
  return _stripePromise;
};

/**
 * ProGate — inline paywall. Wrap any premium content.
 *   <ProGate feature="Parlay Builder">...</ProGate>
 */
export const ProGate = ({ children, feature, compact = false }) => {
  const { isPro, loading } = usePro();
  if (loading) {
    return (
      <div className="flex items-center justify-center p-10 text-gray-400">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }
  if (isPro) return children;
  return <InlinePaywall feature={feature} compact={compact}>{children}</InlinePaywall>;
};

const InlinePaywall = ({ children, feature, compact }) => (
  <div className="relative min-h-[600px]" data-testid="pro-gate">
    <div aria-hidden="true" className="pointer-events-none select-none blur-[5px] opacity-50">
      {children}
    </div>
    <div className="absolute inset-0 flex items-start justify-center pt-12 md:pt-16 z-10">
      <UpgradeCard feature={feature} compact={compact} />
    </div>
  </div>
);

/** Standalone upgrade card — pure inline form, no redirect. */
export const UpgradeCard = ({ feature, compact = false, onSuccess }) => {
  const [stripe, setStripe] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [amount, setAmount] = useState(9);
  const [phase, setPhase] = useState("teaser"); // teaser | form | success
  const [busy, setBusy] = useState(false);

  // Lazy-load Stripe.js on first open
  const begin = async () => {
    setBusy(true);
    try {
      const s = await getStripePromise();
      setStripe(s);
      const { data } = await api.post("/payments/intent", { package_id: "pro_monthly" });
      setClientSecret(data.client_secret);
      setPaymentIntentId(data.payment_intent_id);
      setAmount(data.amount);
      setPhase("form");
    } catch (e) {
      toast.error("Could not start checkout");
    } finally { setBusy(false); }
  };

  if (phase === "success") {
    return <SuccessCard onContinue={onSuccess} />;
  }

  return (
    <div
      className="w-full max-w-md rounded-2xl border border-border bg-gray-0 p-6 shadow-card-lg"
      data-testid="upgrade-card"
    >
      {phase === "form" ? (
        <>
          <button onClick={() => setPhase("teaser")} className="iso-btn-ghost !p-1.5 !text-xs mb-3" data-testid="upgrade-back-btn">
            <ArrowLeft className="size-3.5" /> Back
          </button>
          <Elements
            stripe={stripe}
            options={{
              clientSecret,
              appearance: {
                theme: "stripe",
                variables: {
                  colorPrimary: "#4e36f5",
                  colorBackground: "#ffffff",
                  colorText: "#111111",
                  colorDanger: "#ef4444",
                  fontFamily: "Inter, system-ui, sans-serif",
                  borderRadius: "8px",
                  spacingUnit: "4px",
                },
              },
            }}
          >
            <CheckoutForm
              amount={amount}
              paymentIntentId={paymentIntentId}
              onSuccess={() => setPhase("success")}
            />
          </Elements>
        </>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <div className="flex size-11 items-center justify-center rounded-xl bg-secondary text-white">
              <Crown className="size-5" />
            </div>
            <div>
              <div className="iso-label !mb-0">GameEdge Pro</div>
              <h3 className="font-lexend text-lg font-bold text-gray-900">
                {feature ? `Unlock ${feature}` : "Unlock premium features"}
              </h3>
            </div>
          </div>

          <div className="mt-5 flex items-end gap-1.5">
            <span className="font-lexend text-4xl font-bold text-gray-900">${amount}</span>
            <span className="pb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">/ 30-day pass</span>
          </div>

          {!compact && (
            <ul className="mt-5 space-y-2">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm text-gray-700">
                  <span className="flex size-5 items-center justify-center rounded-full bg-green/15 text-green-dark">
                    <Check className="size-3" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          )}

          <button onClick={begin} disabled={busy} className="iso-btn-primary w-full mt-6" data-testid="upgrade-checkout-btn">
            {busy
              ? <><Loader2 className="size-4 animate-spin" /> Loading…</>
              : <><Sparkles className="size-4" /> Upgrade to Pro</>}
          </button>
          <div className="mt-3 flex items-center justify-center gap-1 text-[11px] text-gray-400">
            <ShieldCheck className="size-3" />
            Secure card payment · Powered by Stripe
          </div>
        </>
      )}
    </div>
  );
};

const CheckoutForm = ({ amount, paymentIntentId, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { refresh } = usePro();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    try {
      // Confirm WITHOUT redirect — stays on page.
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });
      if (confirmError) {
        setError(confirmError.message || "Payment failed");
        setSubmitting(false);
        return;
      }
      if (paymentIntent && paymentIntent.status === "succeeded") {
        // Server-side grant (idempotent) + refresh context
        try {
          await api.post(`/payments/confirm/${paymentIntentId}`);
        } catch (_) { /* webhook / polling will still catch it */ }
        await refresh();
        toast.success("Welcome to Pro 🎉");
        onSuccess?.();
      } else if (paymentIntent) {
        // Async payment (e.g. bank debit) — poll a few times.
        await pollConfirm(paymentIntentId, refresh, onSuccess);
      }
    } catch (err) {
      setError(err.message || "Payment failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="stripe-payment-form">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="iso-label !mb-0">GameEdge Pro</div>
          <div className="font-lexend text-sm font-bold text-gray-900">30-day pass · ${amount}</div>
        </div>
        <Crown className="size-5 text-secondary" />
      </div>

      <div className={`min-h-[140px] ${ready ? "" : "animate-pulse"}`}>
        <PaymentElement
          onReady={() => setReady(true)}
          options={{ layout: "tabs" }}
        />
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-red/30 bg-red/10 p-3 text-xs text-red-dark" data-testid="stripe-error">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || !elements || submitting || !ready}
        className="iso-btn-primary w-full mt-4"
        data-testid="stripe-submit-btn"
      >
        {submitting
          ? <><Loader2 className="size-4 animate-spin" /> Processing…</>
          : <><Sparkles className="size-4" /> Pay ${amount} · Activate Pro</>}
      </button>
      <div className="mt-3 flex items-center justify-center gap-1 text-[11px] text-gray-400">
        <ShieldCheck className="size-3" />
        Your card details go directly to Stripe — we never see them.
      </div>
    </form>
  );
};

async function pollConfirm(id, refresh, onSuccess) {
  for (let i = 0; i < 6; i++) {
    try {
      const { data } = await api.post(`/payments/confirm/${id}`);
      if (data.is_pro) {
        await refresh();
        toast.success("Welcome to Pro 🎉");
        onSuccess?.();
        return;
      }
    } catch (_) { /* keep trying */ }
    await new Promise((r) => setTimeout(r, 1500));
  }
  toast.info("Your payment is processing. Pro will unlock shortly.");
}

const SuccessCard = ({ onContinue }) => (
  <div className="w-full max-w-md rounded-2xl border border-border bg-gray-0 p-6 shadow-card-lg text-center" data-testid="upgrade-success">
    <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-green/15 text-green-dark">
      <Check className="size-7" />
    </div>
    <h3 className="mt-4 font-lexend text-xl font-bold text-gray-900">Welcome to Pro 🎉</h3>
    <p className="mt-2 text-sm text-gray-500">Every premium feature is unlocked for the next 30 days.</p>
    <button onClick={onContinue ?? (() => window.location.reload())} className="iso-btn-primary w-full mt-5" data-testid="upgrade-continue-btn">
      <Sparkles className="size-4" /> Start exploring
    </button>
  </div>
);

/** Small lock chip for menu items / feature titles. */
export const ProChip = ({ className = "" }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full bg-secondary/10 px-2 py-0.5 text-[10px] font-semibold text-secondary ${className}`}
    data-testid="pro-chip"
  >
    <Lock className="size-2.5" /> Pro
  </span>
);
